import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Activity,
  TrendingUp,
  Bot,
  Wallet,
  Flame,
  ArrowUpRight,
  ArrowDownRight,
  ShieldAlert,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtStxoshi(micro) {
  const v = (Number(micro) || 0) / 1_000_000;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function fmtInt(v) {
  return (Number(v) || 0).toLocaleString();
}
function relTime(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function eventIcon(kind) {
  if (kind === "liquidation") return <ShieldAlert className="w-3.5 h-3.5 text-red-400" />;
  if (kind === "big_win") return <ArrowUpRight className="w-3.5 h-3.5 text-green-400" />;
  if (kind === "big_loss") return <ArrowDownRight className="w-3.5 h-3.5 text-red-400" />;
  return <Activity className="w-3.5 h-3.5 text-orange-400" />;
}

/**
 * STXOSHI Pulse — public transparency dashboard.
 * No wallet required. Auto-refreshes every 10s. Every number is derived
 * from live DB state — nothing is hard-coded.
 */
export default function Pulse() {
  const [data, setData] = useState(null);

  const fetchPulse = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/pulse`);
      const d = await r.json();
      setData(d);
    } catch { /* noop — keep last-known-good */ }
  }, []);

  useEffect(() => {
    fetchPulse();
    const t = setInterval(fetchPulse, 10000);
    return () => clearInterval(t);
  }, [fetchPulse]);

  const perps = data?.perps;
  const bots = data?.bots;
  const wallets = data?.wallets;
  const events = data?.recent_events || [];
  const totalOI = (perps?.long_open_interest_stxoshi || 0) + (perps?.short_open_interest_stxoshi || 0);
  const longPct = totalOI > 0 ? (perps.long_open_interest_stxoshi / totalOI) * 100 : 50;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <div className="border-b border-border/40 px-4 py-5 sm:py-8" data-testid="pulse-hero">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-orange-400 animate-pulse" />
            <span className="text-[11px] uppercase tracking-widest text-orange-400 font-mono">
              Live · updates every 10s
            </span>
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">
            STXOSHI Pulse
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Real-time transparency: every number below is derived from live on-app
            activity. This is what&apos;s happening right now.
          </p>
        </div>
      </div>

      <main className="p-3 sm:p-4 md:p-6 max-w-5xl mx-auto space-y-4 pb-24">
        {/* Perps section */}
        <section>
          <h2 className="font-heading text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Perps market
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              icon={<TrendingUp className="w-3.5 h-3.5 text-orange-400" />}
              label="Open positions"
              value={fmtInt(perps?.open_positions)}
              testId="pulse-open-positions"
            />
            <StatCard
              icon={<Activity className="w-3.5 h-3.5 text-orange-400" />}
              label="24h volume"
              value={fmtStxoshi(perps?.volume_24h_stxoshi)}
              sub="STXOSHI"
              testId="pulse-volume-24h"
            />
            <StatCard
              icon={<Flame className="w-3.5 h-3.5 text-orange-400" />}
              label="24 Hour STXOSHI Liquidated"
              value={fmtStxoshi(perps?.liquidated_24h_stxoshi)}
              testId="pulse-liquidated-24h"
            />
            <StatCard
              icon={<ShieldAlert className="w-3.5 h-3.5 text-orange-400" />}
              label="Closes / liquidations 24h"
              value={fmtInt(perps?.closed_24h)}
              testId="pulse-closed-24h"
            />
          </div>

          {/* OI split bar */}
          <Card className="mt-3">
            <CardContent className="p-4">
              <div className="flex items-center justify-between text-[11px] font-mono text-muted-foreground mb-2">
                <span className="text-green-400">
                  LONG {fmtStxoshi(perps?.long_open_interest_stxoshi)}
                </span>
                <span>Total OI {fmtStxoshi(totalOI)}</span>
                <span className="text-red-400">
                  SHORT {fmtStxoshi(perps?.short_open_interest_stxoshi)}
                </span>
              </div>
              <div className="h-2 rounded-full bg-red-500/30 overflow-hidden" data-testid="pulse-oi-bar">
                <div
                  className="h-full bg-green-500"
                  style={{ width: `${longPct}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Perps P&L breakout */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <StatCard
              icon={<ArrowUpRight className="w-3.5 h-3.5 text-green-400" />}
              label="Positive Perp P&L"
              value={`+${fmtStxoshi(perps?.unrealized_positive_pnl_stxoshi)}`}
              sub="STXOSHI · open winners"
              testId="pulse-perps-positive-pnl"
            />
            <StatCard
              icon={<ArrowDownRight className="w-3.5 h-3.5 text-red-400" />}
              label="Negative Perp P&L"
              value={fmtStxoshi(perps?.unrealized_negative_pnl_stxoshi)}
              sub="STXOSHI · open losers"
              testId="pulse-perps-negative-pnl"
            />
            <StatCard
              icon={<ArrowUpRight className="w-3.5 h-3.5 text-green-400" />}
              label="24h realized (positive)"
              value={`+${fmtStxoshi(perps?.realized_positive_pnl_24h_stxoshi)}`}
              sub="STXOSHI · settled wins"
              testId="pulse-perps-realized-positive"
            />
            <StatCard
              icon={<ArrowDownRight className="w-3.5 h-3.5 text-red-400" />}
              label="24h realized (negative)"
              value={fmtStxoshi(perps?.realized_negative_pnl_24h_stxoshi)}
              sub="STXOSHI · settled losses"
              testId="pulse-perps-realized-negative"
            />
          </div>
        </section>

        {/* Bots section */}
        <section>
          <h2 className="font-heading text-xs uppercase tracking-widest text-muted-foreground mb-2">
            AI bots
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard
              icon={<Bot className="w-3.5 h-3.5 text-orange-400" />}
              label="Bots deployed"
              value={fmtInt(bots?.bots_deployed)}
              testId="pulse-bots-deployed"
            />
            <StatCard
              icon={<Bot className="w-3.5 h-3.5 text-green-400" />}
              label="Currently running"
              value={fmtInt(bots?.bots_running)}
              testId="pulse-bots-running"
            />
            <StatCard
              icon={<ArrowUpRight className="w-3.5 h-3.5 text-orange-400" />}
              label="Cumulative bot P&L"
              value={fmtStxoshi(bots?.bots_total_pnl_stxoshi)}
              sub="STXOSHI · net"
              testId="pulse-bots-pnl"
            />
            <StatCard
              icon={<ArrowUpRight className="w-3.5 h-3.5 text-green-400" />}
              label="Positive bots P&L"
              value={`+${fmtStxoshi(bots?.bots_positive_pnl_stxoshi)}`}
              sub="STXOSHI · winners"
              testId="pulse-bots-positive-pnl"
            />
            <StatCard
              icon={<ArrowDownRight className="w-3.5 h-3.5 text-red-400" />}
              label="Negative bots P&L"
              value={fmtStxoshi(bots?.bots_negative_pnl_stxoshi)}
              sub="STXOSHI · losers"
              testId="pulse-bots-negative-pnl"
            />
          </div>
        </section>

        {/* Wallets section */}
        <section>
          <h2 className="font-heading text-xs uppercase tracking-widest text-muted-foreground mb-2">
            User site wallets
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              icon={<Wallet className="w-3.5 h-3.5 text-orange-400" />}
              label="Active wallets"
              value={fmtInt(wallets?.active_wallets)}
              testId="pulse-active-wallets"
            />
            <StatCard
              icon={<Wallet className="w-3.5 h-3.5 text-orange-400" />}
              label="STXOSHI locked in play"
              value={fmtStxoshi(wallets?.stxoshi_locked_in_game_wallets)}
              testId="pulse-stxoshi-locked"
            />
          </div>
        </section>

        {/* Activity tape */}
        <section>
          <h2 className="font-heading text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Live activity
          </h2>
          <Card>
            <CardContent className="p-0">
              {events.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground text-center">
                  No auto-posts yet — the tape lights up on big wins, liquidations, and admin actions.
                </p>
              ) : (
                <ul className="divide-y divide-border" data-testid="pulse-activity-tape">
                  {events.map((e) => (
                    <li key={e.id} className="p-3 flex items-start gap-3 text-xs">
                      <span className="mt-0.5">{eventIcon(e.source_kind)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="whitespace-pre-wrap break-words leading-snug">
                          {e.content}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                          <span className="font-mono">{relTime(e.created_at)}</span>
                          {(e.tags || []).map((t) => (
                            <Badge key={t} variant="outline" className="text-[9px] uppercase">
                              {t}
                            </Badge>
                          ))}
                          <Link
                            to={`/post/${e.id}`}
                            className="ml-auto text-orange-400 hover:underline"
                            data-testid={`pulse-post-link-${e.id}`}
                          >
                            Open →
                          </Link>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Footer disclosure */}
        <p className="text-[11px] text-muted-foreground text-center leading-relaxed pt-4">
          Data source: live MongoDB aggregations. Cached 10s. Last update:{" "}
          {data?.timestamp ? new Date(data.timestamp).toLocaleTimeString() : "—"}
        </p>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, sub, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
          {icon}
          {label}
        </div>
        <p className="font-mono text-xl font-bold">{value ?? "—"}</p>
        {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}
